import { api, apiBlob } from '../../../lib/api';
import type { ListQuery, PagedResult } from '../../administration/users/userTypes';
import type {
  Survey,
  SurveyQuestionnaire,
  SurveyQuestionnaireDetail,
  SurveyQuestionnaireRequest,
  SurveyRequest,
  SurveyRespondent,
  MySurveyResponse,
  SubmitSurveyResponseRequest,
  SurveyResponseState,
  SurveyResponseView,
  SaveDevelopmentPlanRequest,
  SaveImpactAssessmentRequest,
  SurveyQuestionAnswerRow,
  SurveyQuestionnaireSummary,
  CreateSurveyPlanRequest,
  SurveyPlan,
  SurveyPlanDetail,
  SurveyTrainingRecommendation,
  SurveyTrainingRecommendationDetail,
  SurveyTrainingRecommendationPersonnelRow,
  SurveyTrainingRecommendationRequest,
  SurveyTrainingRecommendationTrack,
  TrainingImpactAssessmentInput,
} from './surveyTypes';

function toQueryString(query: ListQuery): string {
  const params = new URLSearchParams();

  if (query.search?.trim()) params.set('search', query.search.trim());
  if (query.page && query.page > 1) params.set('page', String(query.page));
  if (query.pageSize) params.set('pageSize', String(query.pageSize));
  if (query.sort) params.set('sort', query.sort);

  /*
    Sent whenever the key is present, empty string included — an empty value is the reader
    saying "none of these", which matches no row. A truthiness check would drop it and the
    server would read the axis as unmentioned.
  */
  if (query.status !== undefined) params.set('status', query.status);

  const serialized = params.toString();
  return serialized ? `?${serialized}` : '';
}

export const surveysApi = {
  /** Soft-deletes every survey at once; resolves with how many. */
  clear: () => api<number>('/api/surveys/clear-data', { method: 'POST' }),

  list: (query: ListQuery, signal?: AbortSignal) =>
    api<PagedResult<Survey>>(`/api/surveys${toQueryString(query)}`, { signal }),

  get: (id: string, signal?: AbortSignal) => api<Survey>(`/api/surveys/${id}`, { signal }),

  /** Ids of the catalog positions with a post in the named units, or in a unit beneath one. */
  positionIdsInUnits: (orgUnitIds: string[], signal?: AbortSignal) =>
    api<string[]>(`/api/surveys/position-ids?${orgUnitIds.map((id) => `orgUnitIds=${id}`).join('&')}`, { signal }),

  /** Refused with a field error when the division or position is missing or has been deleted. */
  create: (request: SurveyRequest) =>
    api<Survey>('/api/surveys', { method: 'POST', body: request }),

  update: (id: string, request: SurveyRequest) =>
    api<Survey>(`/api/surveys/${id}`, { method: 'PUT', body: request }),

  /** Soft delete: the survey is kept and can be restored from the list. */
  deactivate: (id: string) => api<void>(`/api/surveys/${id}`, { method: 'DELETE' }),

  activate: (id: string) => api<void>(`/api/surveys/${id}/restore`, { method: 'POST' }),

  /** Opens the survey for answers — Not started → Started. */
  start: (id: string) => api<Survey>(`/api/surveys/${id}/start`, { method: 'POST' }),

  /** Opens an ENDED survey again — Done → Started. */
  reopen: (id: string) => api<Survey>(`/api/surveys/${id}/reopen`, { method: 'POST' }),

  /** Closes it to answers — Started → Done. */
  end: (id: string) => api<Survey>(`/api/surveys/${id}/end`, { method: 'POST' }),
};

/**
 * A survey's questionnaires. Reading takes `surveys.read`; every write takes `surveys.update`.
 * A deleted survey's questionnaires are read-only — the writes answer 409.
 */
export const surveyQuestionnairesApi = {
  list: (surveyId: string, query: ListQuery, signal?: AbortSignal) =>
    api<PagedResult<SurveyQuestionnaire>>(`/api/surveys/${surveyId}/questionnaires${toQueryString(query)}`, {
      signal,
    }),

  /** One questionnaire with its questions — what the edit form opens on. */
  get: (surveyId: string, id: string, signal?: AbortSignal) =>
    api<SurveyQuestionnaireDetail>(`/api/surveys/${surveyId}/questionnaires/${id}`, { signal }),

  /** The whole questionnaire, questions included — it is never half-saved. */
  create: (surveyId: string, request: SurveyQuestionnaireRequest) =>
    api<SurveyQuestionnaire>(`/api/surveys/${surveyId}/questionnaires`, { method: 'POST', body: request }),

  update: (surveyId: string, id: string, request: SurveyQuestionnaireRequest) =>
    api<SurveyQuestionnaire>(`/api/surveys/${surveyId}/questionnaires/${id}`, { method: 'PUT', body: request }),

  /** Whether the caller can answer as an employee, and their own response if they have. */
  myResponse: (surveyId: string, id: string, signal?: AbortSignal) =>
    api<SurveyResponseState>(`/api/surveys/${surveyId}/questionnaires/${id}/response`, { signal }),

  /** An employee's CURRENT unit, for defaulting a Division once they are picked — `surveys.respond-on-behalf`. */
  employeeUnit: (personnelId: string, signal?: AbortSignal) =>
    api<string | null>(`/api/surveys/employee-unit/${personnelId}`, { signal }),

  /** Submits the caller's own response, once — `surveys.respond`, and the account must be an employee's. */
  respond: (surveyId: string, id: string, request: SubmitSurveyResponseRequest) =>
    api<MySurveyResponse>(`/api/surveys/${surveyId}/questionnaires/${id}/response`, {
      method: 'POST',
      body: request,
    }),

  /** Soft delete: the questionnaire is kept and can be restored. */
  deactivate: (surveyId: string, id: string) =>
    api<void>(`/api/surveys/${surveyId}/questionnaires/${id}`, { method: 'DELETE' }),

  activate: (surveyId: string, id: string) =>
    api<void>(`/api/surveys/${surveyId}/questionnaires/${id}/restore`, { method: 'POST' }),
};

/**
 * A survey's Responses › Assessment — read only, on `surveys.read`. The rows carry no name; the
 * Name cell resolves one through the persons endpoint, behind `personnel.read`.
 */
export const surveyRespondentsApi = {
  list: (surveyId: string, query: ListQuery, signal?: AbortSignal) =>
    api<PagedResult<SurveyRespondent>>(`/api/surveys/${surveyId}/respondents${toQueryString(query)}`, {
      signal,
    }),

  /** One response, every answer in words. 403 for an employee above the reader's access level. */
  get: (surveyId: string, id: string, signal?: AbortSignal) =>
    api<SurveyResponseView>(`/api/surveys/${surveyId}/respondents/${id}`, { signal }),

  /** Marks a response as assessed, once — `surveys.assess`. The remarks are optional. */
  assess: (surveyId: string, id: string, remarks: string | null) =>
    api<SurveyResponseView>(`/api/surveys/${surveyId}/respondents/${id}/assess`, {
      method: 'POST',
      body: { remarks },
    }),

  /** Saves the identified development needs and the plan together — `surveys.plan`. The plan is sanitised on write. */
  saveDevelopmentPlan: (surveyId: string, id: string, request: SaveDevelopmentPlanRequest) =>
    api<SurveyResponseView>(`/api/surveys/${surveyId}/respondents/${id}/development-plan`, {
      method: 'PUT',
      body: request,
    }),

  /** Responses › Summary — one questionnaire's questions with their counts or written answers. */
  summary: (surveyId: string, questionnaireId: string, signal?: AbortSignal) =>
    api<SurveyQuestionnaireSummary>(`/api/surveys/${surveyId}/questionnaires/${questionnaireId}/summary`, { signal }),

  /** Responses › Question — every respondent's answer to one question, paged and masked. */
  questionAnswers: (surveyId: string, questionnaireId: string, questionId: string, query: ListQuery, signal?: AbortSignal) =>
    api<PagedResult<SurveyQuestionAnswerRow>>(
      `/api/surveys/${surveyId}/questionnaires/${questionnaireId}/questions/${questionId}/answers${toQueryString(query)}`,
      { signal },
    ),

  /** Puts a written plan to its approver — `surveys.submit-plan`. Locks it while they hold it. */
  submitDevelopmentPlan: (surveyId: string, id: string) =>
    api<SurveyResponseView>(`/api/surveys/${surveyId}/respondents/${id}/development-plan/submit`, { method: 'POST' }),

  /** Records — or revises — a training's impact assessment — `surveys.assess-impact`. Refused before the employee's report. */
  saveImpactAssessment: (surveyId: string, id: string, request: SaveImpactAssessmentRequest) =>
    api<SurveyResponseView>(`/api/surveys/${surveyId}/respondents/${id}/impact-assessment`, { method: 'PUT', body: request }),

  /** Sends an APPROVED plan to the employee it is about — `surveys.send-plan`. Once; refused before approval. */
  sendDevelopmentPlan: (surveyId: string, id: string) =>
    api<SurveyResponseView>(`/api/surveys/${surveyId}/respondents/${id}/development-plan/send`, { method: 'POST' }),

  /** Survey › Development Plan — the assessed live responses, masked as the respondent list is. */
  developmentPlan: (surveyId: string, query: ListQuery, signal?: AbortSignal) =>
    api<PagedResult<SurveyRespondent>>(`/api/surveys/${surveyId}/development-plan${toQueryString(query)}`, {
      signal,
    }),

  /** Soft delete: the response and its answers are kept, and the employee may answer again. */
  deactivate: (surveyId: string, id: string) =>
    api<void>(`/api/surveys/${surveyId}/respondents/${id}`, { method: 'DELETE' }),

  /** Refused while the employee has a live response to the same questionnaire again. */
  activate: (surveyId: string, id: string) =>
    api<void>(`/api/surveys/${surveyId}/respondents/${id}/restore`, { method: 'POST' }),
};

/**
 * Survey › Development Plan — plans created ONCE for several assessed employees. Read on
 * `surveys.read`; Create writes development plans, so it is `surveys.plan`. Each chosen employee
 * gets their own copy, revised and approved through their response as before.
 */
export const surveyPlansApi = {
  list: (surveyId: string, query: ListQuery, signal?: AbortSignal) =>
    api<PagedResult<SurveyPlan>>(`/api/surveys/${surveyId}/plans${toQueryString(query)}`, { signal }),

  get: (surveyId: string, planId: string, signal?: AbortSignal) =>
    api<SurveyPlanDetail>(`/api/surveys/${surveyId}/plans/${planId}`, { signal }),

  /** Adds assessed employees to an existing plan — each gets their own copy, all or none. */
  addMembers: (surveyId: string, planId: string, request: { respondentIds: string[]; personnelIds: string[] }) =>
    api<SurveyPlanDetail>(`/api/surveys/${surveyId}/plans/${planId}/members`, { method: 'POST', body: request }),

  /** All or none: every chosen employee gets the plan, or the request is refused whole. */
  create: (surveyId: string, request: CreateSurveyPlanRequest) =>
    api<SurveyPlanDetail>(`/api/surveys/${surveyId}/plans`, { method: 'POST', body: request }),
};

/**
 * A development plan's Training Recommendations — each its own record with its own approval. Read
 * on `surveys.read`; create, update and delete on `surveys.plan`; submit on `surveys.submit-plan`.
 * Nothing here marks one approved: the approval engine's decision does.
 *
 * `get`, `personnel` and `track` answer `training-recommendations.read` as well — the register
 * across every plan opens the same record. Its own list is `trainingRecommendationsApi`.
 */
export const surveyTrainingRecommendationsApi = {
  list: (planId: string, query: ListQuery, signal?: AbortSignal) =>
    api<PagedResult<SurveyTrainingRecommendation>>(
      `/api/survey-plans/${planId}/training-recommendations${toQueryString(query)}`,
      { signal },
    ),

  get: (id: string, signal?: AbortSignal) =>
    api<SurveyTrainingRecommendationDetail>(`/api/training-recommendations/${id}`, { signal }),

  create: (planId: string, request: SurveyTrainingRecommendationRequest) =>
    api<SurveyTrainingRecommendationDetail>(`/api/survey-plans/${planId}/training-recommendations`, {
      method: 'POST',
      body: request,
    }),

  /** Refused while an approver holds it and once approved. */
  update: (id: string, request: SurveyTrainingRecommendationRequest) =>
    api<SurveyTrainingRecommendationDetail>(`/api/training-recommendations/${id}`, { method: 'PUT', body: request }),

  /** Soft delete; a pending approval is closed with it. Refused once approved. */
  deactivate: (id: string) => api<void>(`/api/training-recommendations/${id}`, { method: 'DELETE' }),

  /** The employees on its plan, each with their own track on it — the Personnel table. */
  personnel: (id: string, query: ListQuery, signal?: AbortSignal) =>
    api<PagedResult<SurveyTrainingRecommendationPersonnelRow>>(
      `/api/training-recommendations/${id}/personnel${toQueryString(query)}`,
      { signal },
    ),

  /** One employee's answer, training and report on it. 403 above the reader's access level. */
  track: (id: string, respondentId: string, signal?: AbortSignal) =>
    api<SurveyTrainingRecommendationTrack>(`/api/training-recommendations/${id}/personnel/${respondentId}`, { signal }),

  /** FETCHES one file an employee attached to their report — `development-plans.download`. Never a link. */
  downloadTrackAttachment: (id: string, respondentId: string, attachmentId: string) =>
    apiBlob(`/api/training-recommendations/${id}/personnel/${respondentId}/report/attachments/${attachmentId}`),

  /** Sends an APPROVED recommendation to the employees on its plan. Once. */
  /** Records or revises this training's impact on several employees, all or none — `surveys.assess-impact`. */
  saveImpactAssessments: (id: string, items: TrainingImpactAssessmentInput[]) =>
    api<void>(`/api/training-recommendations/${id}/impact-assessments`, { method: 'PUT', body: { items } }),

  send: (id: string) =>
    api<SurveyTrainingRecommendationDetail>(`/api/training-recommendations/${id}/send`, { method: 'POST' }),

  /** Puts it to its approver under Approvals. */
  submit: (id: string) =>
    api<SurveyTrainingRecommendationDetail>(`/api/training-recommendations/${id}/submit`, { method: 'POST' }),
};
